import { useCallback, useMemo, useState } from 'react';
import { Form } from 'antd';
import type { FormInstance } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import type { OptimizePlanConfig } from '../../../../config/optimizePlanConfig';
import type { FormData } from '../../components/OptimizePlan/types';
import { INITIAL_FORM_DATA } from '../../components/OptimizePlan/types';
import { buildScopeFilters } from '../../utils/optimizePayloadBuilder';
import type { ScopeSelection } from '../../utils/optimizePayloadBuilder';
import type { ConstraintValuesContext } from '../queries/useConstraintOriginalValues';

dayjs.extend(utc);

interface UseOptimizePlanFormParams {
    form: FormInstance;
    planId: string;
    config: OptimizePlanConfig;
}

export function useOptimizePlanForm({ form, planId, config }: UseOptimizePlanFormParams) {
    // Build a sorted list of (order, fieldName, attributeType) triples, keyed by depthLevel.
    const depthLevelPairs = useMemo(
        () =>
            config.scopeOfOptimisationFields
                .filter((f) => f.name != null && f.depthLevel != null)
                .map((f) => ({
                    order: f.depthLevel!,
                    fieldName: f.name!,
                    attributeType: f.attributeType,
                }))
                .sort((a, b) => a.order - b.order),
        [config.scopeOfOptimisationFields],
    );

    const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);

    const allFormValues = Form.useWatch([], form) as Record<string, unknown> | undefined;

    const scopeSelections: ScopeSelection[] = useMemo(
        () =>
            depthLevelPairs.map(({ fieldName, attributeType }) => {
                const val = allFormValues?.[fieldName];
                return { attributeType, values: Array.isArray(val) ? (val as string[]) : [] };
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allFormValues, depthLevelPairs.map((p) => p.fieldName).join(',')],
    );

    const scopeFilters = useMemo(() => buildScopeFilters(scopeSelections), [scopeSelections]);

    const promotionCountStartDate =
        formData.optimization_start_date != null
            ? dayjs.unix(formData.optimization_start_date).utc().startOf('day').unix()
            : null;

    /**
     * The Scope tab sets a floor on how coarse a constraint level may be: an option is
     * disabled once any scope field at or below its `disableAtDepthLevel` has values.
     * Since each level's `disableAtDepthLevel` is its own `depthLevel + 1`, the net
     * effect is that the deepest scope selection becomes the coarsest level selectable.
     */
    const levelOptions = useMemo(
        () =>
            config.constraintLevelOptions.map((opt) => ({
                ...opt,
                disabled:
                    opt.disableAtDepthLevel != null
                        ? scopeSelections.some(
                              (s, i) => depthLevelPairs[i]?.order >= opt.disableAtDepthLevel! && s.values.length > 0,
                          )
                        : false,
            })),
        [scopeSelections, config.constraintLevelOptions, depthLevelPairs],
    );

    /**
     * The `get_optimization_original_vals` request. `null` until the objective and
     * start date are set, which is what gates the fetch.
     *
     * `level` is that same floor — the coarsest option the scope still allows, which
     * is the first one not disabled since the options are authored coarsest-first.
     * The response then covers that level and every level below it, so one call
     * serves every level block the user can go on to add.
     *
     * `attribute_list` narrows that level to the attributes the user actually scoped
     * to, so it must come from the deepest scope field that has values — the same
     * selection that set the floor. Empty scope sends `[]`, meaning "all".
     */
    const constraintValuesContext = useMemo<ConstraintValuesContext | null>(() => {
        if (!formData.optimization_start_date || !formData.objective) return null;

        const level = levelOptions.find((opt) => !opt.disabled)?.key;
        if (level == null) return null;

        let attributeList: string[] = [];
        for (const selection of scopeSelections) {
            if (selection.values.length > 0) attributeList = selection.values;
        }

        return {
            planId,
            objective: formData.objective,
            startDate: formData.optimization_start_date,
            level,
            attributeList,
        };
    }, [formData.objective, formData.optimization_start_date, scopeSelections, levelOptions, planId]);

    const resetForm = useCallback(() => {
        setFormData(INITIAL_FORM_DATA);
        form.resetFields();
    }, [form]);

    return {
        formData,
        setFormData,
        levelOptions,
        scopeSelections,
        scopeFilters,
        promotionCountStartDate,
        constraintValuesContext,
        resetForm,
    };
}
